import { useState } from 'react';
import { Modal, Button, Radio, Input, Space, message } from 'antd';
import { createPortalLinkFunc } from '../../lib/firebase/firebase';
import { trackIntercomEvent } from '../../lib/intercom/intercom';

const { TextArea } = Input;

const CANCEL_REASONS = [
  { value: 'too_expensive', label: "It's too expensive" },
  { value: 'not_using', label: "I'm not using it enough" },
  { value: 'missing_features', label: "It's missing features I need" },
  { value: 'hard_to_use', label: "It's too difficult to use" },
  { value: 'found_alternative', label: 'I found a better alternative' },
  { value: 'temporary', label: "I only needed it temporarily" },
  { value: 'other', label: 'Other' },
];

export default function CancelInterceptModal({ open, onClose }) {
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('reason');

  const handleSubmitAndRedirect = async () => {
    setLoading(true);
    try {
      trackIntercomEvent('cancellation_intent', {
        reason,
        details: details.substring(0, 500),
      });

      const { data } = await createPortalLinkFunc({
        returnUrl: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (error) {
      console.error('Error opening customer portal:', error);
      message.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleNeedHelp = () => {
    trackIntercomEvent('cancellation_help_requested', { reason });
    if (window.Intercom) {
      window.Intercom('showNewMessage',
        `I'm considering cancelling because: ${CANCEL_REASONS.find(r => r.value === reason)?.label || reason}. ${details}`
      );
    }
    onClose();
    resetState();
  };

  const resetState = () => {
    setReason(null);
    setDetails('');
    setStep('reason');
    setLoading(false);
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={step === 'reason' ? 'Before you go...' : 'Can we help?'}
      centered
      width={480}
    >
      {step === 'reason' && (
        <div style={{ padding: '8px 0' }}>
          <p style={{ marginBottom: 16, color: '#555' }}>
            We'd love to understand why you're looking to cancel so we can improve SvgTrace.
          </p>
          <Radio.Group
            onChange={(e) => setReason(e.target.value)}
            value={reason}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {CANCEL_REASONS.map((r) => (
                <Radio key={r.value} value={r.value} style={{ padding: '4px 0' }}>
                  {r.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>

          {reason && (
            <TextArea
              placeholder="Tell us more (optional)"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              style={{ marginTop: 16 }}
              maxLength={500}
            />
          )}

          <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>Never mind</Button>
            <Button
              type="primary"
              disabled={!reason}
              onClick={() => setStep('offer')}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 'offer' && (
        <div style={{ padding: '8px 0' }}>
          {(reason === 'hard_to_use' || reason === 'missing_features') ? (
            <p style={{ marginBottom: 16, color: '#555' }}>
              We'd love to help you get the most out of SvgTrace. Would you like to chat with our team? They can walk you through features or help with your specific project.
            </p>
          ) : reason === 'too_expensive' ? (
            <p style={{ marginBottom: 16, color: '#555' }}>
              We understand budget matters. Before you cancel, would you like to reach out to our team? We may be able to help find a solution that works for you.
            </p>
          ) : (
            <p style={{ marginBottom: 16, color: '#555' }}>
              Thanks for the feedback. If there's anything we can help with before you go, our team is here for you.
            </p>
          )}

          <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button onClick={handleNeedHelp} style={{ color: '#667eea', borderColor: '#667eea' }}>
              Chat with us first
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleSubmitAndRedirect}
              loading={loading}
            >
              Continue to cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
